import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { ModalBase } from '@components/Modal';
import ExpandedSection from '@components/ExpandedSection';
import ImportLoadingOverlay from '@components/ImportLoadingOverlay';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import { useTranslation, usePermissions, useLocations } from '@hooks';
import { handleApiError } from '@helpers/api';
import { generateCompanyCardAddress } from '@helpers/companies';
import { linkedinTargetLabelKey } from '@helpers/integrations';
import { AccIntegrationName, TwoStepIntegrationConfig, AccountIntegrationsDictionary } from '@constants/integrations';
import {
    accountActions,
    alertActions,
    googleActions,
    facebookActions,
    coverManagerActions,
    vtexActions,
    linkedinActions,
} from '@actions';
import {
    xIntegrationService,
    googleIntegrationService,
    facebookIntegrationService,
    coverManagerIntegrationService,
    vtexIntegrationService,
    linkedinIntegrationService,
    squareIntegrationService,
    companyService,
    connectLocationIntegration,
    applyLocationIntegrationMappings,
    buildConnectMappingOperation,
    buildDisconnectMappingOperation,
} from '@services';
import ProfileRow from './ProfileRow';
import { LINK_VALUE, SKIP_VALUE, CREATE_VALUE, isLinkedToType } from './constants';
import { captureLocationsImported } from '@helpers/product-events';

const joinAddress = item => [item.address, item.city, item.postalCode, item.country].filter(Boolean).join(', ');

const asProfileList = data => {
    if (Array.isArray(data)) return data;
    return data ? [data] : [];
};

const LIST_HANDLERS = {
    [AccIntegrationName.Google]: {
        fetch: googleIntegrationService.getLocations,
        invalidate: googleActions.reset,
        normalize: item => ({
            key: item.locationId,
            title: item.title,
            accountName: item.accountName,
            address: item.country ? generateCompanyCardAddress(item) : null,
            subtitle: item.primaryPhoneNumber || null,
            unverified: !item.isVerified,
            connected: item.connected,
            connectedLocationName: item.connectedLocationName,
            connectedLocationId: item.connectedLocationId,
            raw: item,
        }),
    },
    [AccIntegrationName.Facebook]: {
        fetch: facebookIntegrationService.getPages,
        invalidate: facebookActions.reset,
        normalize: item => ({
            key: item.id,
            title: item.name,
            subtitle: item.username ? `@${item.username}` : null,
            connected: item.connected,
            connectedLocationName: item.connectedLocationName,
            connectedLocationId: item.connectedLocationId,
            raw: item,
        }),
    },
    [AccIntegrationName.CoverManager]: {
        fetch: coverManagerIntegrationService.getRestaurants,
        invalidate: coverManagerActions.reset,
        normalize: item => ({
            key: item.slug,
            title: item.name,
            address: joinAddress(item),
            connected: item.connected,
            connectedLocationName: item.connectedLocationName,
            connectedLocationId: item.connectedLocationId,
            raw: item,
        }),
    },
    [AccIntegrationName.VTEX]: {
        fetch: vtexIntegrationService.getAccounts,
        invalidate: vtexActions.reset,
        normalize: item => ({
            key: item.accountName,
            title: item.accountName,
            connected: item.connected,
            connectedLocationName: item.connectedLocationName,
            connectedLocationId: item.connectedLocationId,
            raw: item,
        }),
    },
    [AccIntegrationName.LinkedIn]: {
        fetch: linkedinIntegrationService.getTargets,
        invalidate: linkedinActions.reset,
        normalize: (item, t) => {
            const typeLabel = t(linkedinTargetLabelKey(item.type));
            return {
                key: item.urn,
                title: item.name || typeLabel,
                subtitle: typeLabel,
                avatar: item.imageUrl,
                connected: item.connected,
                connectedLocationName: item.connectedLocationName,
                connectedLocationId: item.connectedLocationId,
                raw: item,
            };
        },
    },
    [AccIntegrationName.Square]: {
        fetch: squareIntegrationService.getLocations,
        normalize: (item, t) => ({
            key: item.id,
            title: item.name,
            address: item.address,
            subtitle: item.active ? null : t('SettingsIntegrations.manageIntegration.inactiveLocation'),
            connected: item.connected,
            connectedLocationName: item.connectedLocationName,
            connectedLocationId: item.connectedLocationId,
            raw: item,
        }),
    },
};

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 564,
    },
    '& .modal-base-content': {
        padding: '8px 24px',
    },
});

const Progress = styled(CircularProgress)({
    color: 'var(--text-tertiary)',
    width: '58px !important',
    height: '58px !important',
    display: 'block',
    margin: '24px auto',
});

const ExpandedSectionWrapper = styled(ExpandedSection)({
    marginBottom: '16px',
});

const List = styled('div')({
    display: 'grid',
});

const ErrorState = styled('div')({
    display: 'grid',
    gridGap: '12px',
    justifyItems: 'start',
    padding: '16px 0',
});

const ManageIntegrationModal = ({ integration, integrationId, initialConnect, onClose, onDisconnect }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { hasFullSettings, hasFullAccess, hasGlobalScope } = usePermissions();
    const { getWritableSelectLocationsOptions, locationsMap } = useLocations();

    const config = TwoStepIntegrationConfig[integration];
    const canManage = hasFullSettings();
    const canCreateLocations = hasFullAccess('locations') && hasGlobalScope;

    const [modes, setModes] = useState({});
    const [linkLocation, setLinkLocation] = useState({});
    const [section, setSection] = useState({});
    const [accountInfo, setAccountInfo] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [listItems, setListItems] = useState(null);
    const [hasListError, setHasListError] = useState(false);
    const seedRef = useRef({ modes: {}, locations: {} });
    const loadTokenRef = useRef(0);

    const account = useSelector(state => state.account?.account);

    const integrationName = AccountIntegrationsDictionary.find(i => i.id === integration)?.name;

    const createSlots = useMemo(() => {
        if (!config?.supportsCreate || !account) return 0;
        const max = account.companiesMaxCount;
        if (max == null || max === Infinity) return Infinity;
        return Math.max(0, max - (account.companies?.length || 0));
    }, [config, account]);

    const allLocationGroups = useMemo(
        () => getWritableSelectLocationsOptions('locations'),
        [getWritableSelectLocationsOptions],
    );

    const connectedToTypeIds = useMemo(() => {
        const ids = new Set();
        if (!config) return ids;
        Object.values(locationsMap).forEach(loc => {
            if (isLinkedToType(loc, config.locationIntegration)) ids.add(loc.companyId);
        });
        return ids;
    }, [locationsMap, config]);

    const loadProfiles = () => {
        loadTokenRef.current += 1;
        const token = loadTokenRef.current;
        setListItems(null);
        setHasListError(false);
        LIST_HANDLERS[integration]
            .fetch(integrationId)
            .then(data => {
                if (loadTokenRef.current !== token) return;
                setListItems(asProfileList(data));
            })
            .catch(() => {
                if (loadTokenRef.current !== token) return;
                setListItems([]);
                setHasListError(true);
            });
    };

    useEffect(() => {
        if (config?.listProfiles) loadProfiles();
    }, [integration, integrationId]);

    useEffect(() => {
        if (config?.previewService !== 'x') return;
        xIntegrationService
            .getAccountInfo(integrationId)
            .then(setAccountInfo)
            .catch(() => setAccountInfo(null));
    }, [integration, integrationId]);

    const profiles = useMemo(() => {
        if (!config) return [];
        if (config.listProfiles) {
            return (listItems || []).map(item => LIST_HANDLERS[integration].normalize(item, t));
        }
        // Scope to the connected account being managed so a second X/TikTok account never shows the
        // first account's linked location (the single-profile flow has no per-resource list).
        const connectedLocation = Object.values(locationsMap).find(loc =>
            isLinkedToType(loc, config.locationIntegration, integrationId),
        );
        return [
            {
                key: 'single',
                title: accountInfo?.displayName || accountInfo?.handle || integrationName,
                subtitle: accountInfo?.handle
                    ? `@${accountInfo.handle}`
                    : t('SettingsIntegrations.manageIntegration.singleProfilePrompt'),
                avatar: accountInfo?.profileImageUrl,
                connected: !!connectedLocation,
                connectedLocationName: connectedLocation?.internalName,
                connectedLocationId: connectedLocation?.companyId,
            },
        ];
    }, [config, integration, integrationId, listItems, accountInfo, locationsMap, integrationName, t]);

    const showCreate = !!config?.supportsCreate && canCreateLocations;

    useEffect(() => {
        const missing = profiles.filter(p => !(p.key in seedRef.current.modes));
        if (!missing.length) return;
        const nextModes = {};
        const nextLocations = {};
        missing.forEach(p => {
            const mode = p.connected || initialConnect ? LINK_VALUE : SKIP_VALUE;
            const location = p.connected ? p.connectedLocationId || '' : '';
            nextModes[p.key] = mode;
            nextLocations[p.key] = location;
            seedRef.current.modes[p.key] = mode;
            seedRef.current.locations[p.key] = location;
        });
        setModes(prev => ({ ...prev, ...nextModes }));
        setLinkLocation(prev => ({ ...prev, ...nextLocations }));
    }, [profiles, initialConnect]);

    useEffect(() => {
        if (!config?.groupByAccount || !profiles.length) return;
        setSection(prev => {
            const missing = [...new Set(profiles.map(p => p.accountName))].filter(a => !(a in prev));
            if (!missing.length) return prev;
            const next = { ...prev };
            missing.forEach(a => {
                next[a] = true;
            });
            return next;
        });
    }, [profiles, config]);

    const handleModeChange = (key, value) => setModes(prev => ({ ...prev, [key]: value }));
    const handleLocationChange = (key, value) => setLinkLocation(prev => ({ ...prev, [key]: value }));

    const optionsForRow = key => {
        const ownId = seedRef.current.locations[key];
        const targetedByOther = new Set(
            profiles
                .filter(p => p.key !== key && modes[p.key] === LINK_VALUE && linkLocation[p.key])
                .map(p => linkLocation[p.key]),
        );
        const releasedInEdit = new Set();
        profiles.forEach(p => {
            const seed = seedRef.current.locations[p.key];
            if (seed && !(modes[p.key] === LINK_VALUE && linkLocation[p.key] === seed)) {
                releasedInEdit.add(seed);
            }
        });
        return allLocationGroups
            .map(group => ({
                group: group.group,
                children: group.children.filter(c => {
                    if (targetedByOther.has(c.value)) return false;
                    if (c.value === ownId) return true;
                    return !connectedToTypeIds.has(c.value) || releasedInEdit.has(c.value);
                }),
            }))
            .filter(group => group.children.length);
    };

    const changedCount = profiles.filter(p => {
        const mode = modes[p.key];
        if (mode === undefined) return false;
        if (mode !== seedRef.current.modes[p.key]) return true;
        return mode === LINK_VALUE && linkLocation[p.key] !== seedRef.current.locations[p.key];
    }).length;

    const hasUnresolved = profiles.some(p => modes[p.key] === LINK_VALUE && !linkLocation[p.key]);

    // The location-integration row id to disconnect, scoped to the account being managed so we never
    // tear down a sibling account's link for the same location/type.
    const integrationIdFor = locationId =>
        locationsMap[locationId]?.integrations?.find(
            i => i.type === config.locationIntegration && (!integrationId || i.integrationId === integrationId),
        )?.id;

    const handleSubmit = async () => {
        setIsSubmitting(true);

        const operations = [];
        const creates = [];
        profiles.forEach(p => {
            const mode = modes[p.key];
            const location = linkLocation[p.key];
            const seedLocation = seedRef.current.locations[p.key];
            const oldLocationId = seedRef.current.modes[p.key] === LINK_VALUE && seedLocation ? seedLocation : null;

            if (mode === CREATE_VALUE) {
                creates.push({ raw: p.raw, oldLocationId });
                return;
            }
            const disconnectOld = () => {
                const locationIntegrationId = integrationIdFor(oldLocationId);
                if (locationIntegrationId) {
                    operations.push(
                        buildDisconnectMappingOperation(
                            config.locationIntegration,
                            oldLocationId,
                            locationIntegrationId,
                        ),
                    );
                }
            };
            if (mode === SKIP_VALUE) {
                if (oldLocationId) disconnectOld();
                return;
            }
            if (!location || oldLocationId === location) return;
            if (oldLocationId) disconnectOld();
            operations.push(buildConnectMappingOperation(config.locationIntegration, location, p.raw, integrationId));
        });

        let hadError = false;

        if (operations.length) {
            try {
                await applyLocationIntegrationMappings(operations);
            } catch (error) {
                hadError = true;
                handleApiError(error);
            }
        }

        if (!hadError && creates.length) {
            let importedLocations = 0;
            setProgress({ current: 0, total: creates.length });
            for (let i = 0; i < creates.length; i += 1) {
                const create = creates[i];
                let freed = false;
                if (create.oldLocationId) {
                    const locationIntegrationId = integrationIdFor(create.oldLocationId);
                    if (locationIntegrationId) {
                        try {
                            await companyService.disconnectIntegration(create.oldLocationId, locationIntegrationId);
                            freed = true;
                        } catch (error) {
                            hadError = true;
                            handleApiError(error);
                            setProgress(prev => ({ ...prev, current: i + 1 }));
                            continue;
                        }
                    }
                }
                let importFailed = false;
                try {
                    const result = await companyService.importGoogleCompanies([create.raw]);
                    importedLocations += result?.importedCount ?? 0;
                    importFailed = !result?.importedCount;
                    if (importFailed) {
                        handleApiError(
                            { errorCode: result?.skippedLocations?.[0]?.reason },
                            'alertMessages.importFail',
                        );
                    }
                } catch (error) {
                    importFailed = true;
                    handleApiError(error, 'alertMessages.importFail');
                }
                if (importFailed) {
                    hadError = true;
                    if (freed) {
                        try {
                            await connectLocationIntegration(
                                config.locationIntegration,
                                create.oldLocationId,
                                create.raw,
                                integrationId,
                            );
                        } catch (rollbackError) {
                            handleApiError(rollbackError);
                        }
                    }
                }
                setProgress(prev => ({ ...prev, current: i + 1 }));
            }
            captureLocationsImported({ source: 'google', count: importedLocations });
        }

        await dispatch(accountActions.get());
        const { invalidate } = LIST_HANDLERS[integration] || {};
        if (config.listProfiles && invalidate) dispatch(invalidate());
        setIsSubmitting(false);

        if (hadError) {
            seedRef.current = { modes: {}, locations: {} };
            setModes({});
            setLinkLocation({});
            if (config.listProfiles) loadProfiles();
            return;
        }

        dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        onClose();
    };

    const renderRow = profile => {
        const createUsedElsewhere = profiles.filter(p => p.key !== profile.key && modes[p.key] === CREATE_VALUE).length;
        return (
            <ProfileRow
                key={profile.key}
                profile={profile}
                mode={modes[profile.key] ?? (profile.connected || initialConnect ? LINK_VALUE : SKIP_VALUE)}
                location={linkLocation[profile.key] ?? ''}
                options={optionsForRow(profile.key)}
                showCreate={showCreate}
                createDisabled={createUsedElsewhere >= createSlots}
                onModeChange={handleModeChange}
                onLocationChange={handleLocationChange}
                disabled={!canManage}
            />
        );
    };

    const isLoading = config?.listProfiles && listItems === null;

    const renderContent = () => {
        if (isLoading) return <Progress />;
        if (!profiles.length && hasListError) {
            return (
                <ErrorState>
                    <Typography variant="body2" color="textSecondary">
                        {t('SettingsIntegrations.manageIntegration.loadError')}
                    </Typography>
                    <Button variant="outlined" onClick={loadProfiles}>
                        {t('SettingsIntegrations.manageIntegration.retry')}
                    </Button>
                </ErrorState>
            );
        }
        if (!profiles.length) {
            return (
                <Typography variant="body2" color="textSecondary" sx={{ display: 'block', padding: '16px 0' }}>
                    {t('SettingsIntegrations.manageIntegration.emptyProfiles')}
                </Typography>
            );
        }
        if (config.groupByAccount) {
            const byAccount = Object.groupBy(profiles, p => p.accountName);
            return Object.entries(byAccount).map(([accountName, accountProfiles]) => (
                <ExpandedSectionWrapper
                    key={accountName}
                    isOpen={section[accountName]}
                    onClick={() => setSection(prev => ({ ...prev, [accountName]: !prev[accountName] }))}
                    text={accountName}
                >
                    <List>{accountProfiles.map(renderRow)}</List>
                </ExpandedSectionWrapper>
            ));
        }
        return <List>{profiles.map(renderRow)}</List>;
    };

    return (
        <>
            <ImportLoadingOverlay open={isSubmitting} current={progress.current} total={progress.total} />
            <Modal
                fullScreen
                isOpen
                handleClose={onClose}
                isDirty={changedCount > 0}
                title={t('SettingsIntegrations.manageIntegration.title')}
                subtitle={t('SettingsIntegrations.manageIntegration.subtitle')}
                onAdditionalAction={canManage ? () => onDisconnect(integration, integrationId) : undefined}
                additionalTextButton={t('buttons.delete')}
                additionalButtonType="danger"
                additionalActionDisabled={isSubmitting}
                onPrimaryAction={handleSubmit}
                primaryActionText={t('buttons.save')}
                primaryActionDisabled={!changedCount || hasUnresolved || isSubmitting || !canManage}
            >
                {renderContent()}
            </Modal>
        </>
    );
};

export default ManageIntegrationModal;
